import { useCallback, useEffect, useRef, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import {
  emptyAnswers,
  emptyPersonFacts,
  type Answers,
  type Assessment,
  type Policy,
} from '@asvise/household-engine/vocabulary'
import { Auth } from './components/Auth'
import { EditingContext, useEditingState } from './components/editing'
import { Household, People, hasSomebody } from './components/Household'
import { PolicyImport } from './components/PolicyImport'
import { Board } from './components/Board'
import { FindingSheet } from './components/Protection'
import { PolicySheet } from './components/PolicySheet'
import { keyFor, useAssessment } from './lib/assess'
import {
  applyTerms,
  listConsents,
} from './lib/consent'
import { currentHousehold, loadRecord, saveAssessment, savePolicies, saveRecord } from './lib/store'
import { EXPLANATION_VERSION } from './lib/language'
import { supabase, supabaseConfigured } from './lib/supabase'

/**
 * The household navigator.
 *
 * Structured on the business app's blueprint, with one thing done differently
 * from the start. That app admits in a comment that signing out leaves
 * component state behind, so one account could see another's record, and that
 * it works around this by reloading the window; it names the real fix as
 * keying the signed-in view by user id and letting React throw the state away.
 *
 * `<Signed key={session.user.id}>` is that fix. There is nothing to clear on
 * sign-out because there is nothing to carry over: a different user is a
 * different component.
 */
/**
 * The board is the main screen, and the questionnaire is not.
 *
 * It was the other way round: a household signed in to twenty questions and
 * learned nothing until it finished them. BOARD opens on the six protection
 * areas with what each one can say and what it would need to say more, RECORD
 * is still there for anybody who would rather answer everything at once, and
 * the rest are reached from the board.
 */
type View = 'BOARD' | 'RECORD' | 'DOCUMENTS'

/**
 * Whether the record has what is on screen, said at the foot of the screen.
 *
 * The business app's component, its wording and its placement: one of these
 * sits in the `.actions` row at the bottom of every screen that can change an
 * answer, which over there is the board, the record and the policy form. It was
 * briefly in this app's header instead, beside the account, where nothing it
 * reports is happening.
 *
 * Two states fewer than the business app has. There is no QUEUED, because this
 * app has no offline queue, and an error is not a state here: a failed write
 * goes to the banner at the top with everything else that went wrong, rather
 * than being reported quietly at the foot of the page.
 */
function SaveStatus({
  saving,
  unsaved,
  savedAt,
}: {
  saving: boolean
  unsaved: boolean
  savedAt: number | null
}) {
  const busy = saving || unsaved
  return (
    <p className={`save-status ${busy ? 'saving' : ''}`}>
      {busy
        ? 'Saving…'
        : savedAt === null
          ? 'Changes save automatically'
          : `All changes saved · ${new Date(savedAt).toLocaleTimeString('en-SG', {
              hour: 'numeric',
              minute: '2-digit',
            })}`}
    </p>
  )
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!supabase) {
      setReady(true)
      return
    }
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setReady(true)
    })
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next))
    return () => data.subscription.unsubscribe()
  }, [])

  if (!supabaseConfigured) {
    return (
      <main className="shell">
        <h1>Not configured</h1>
        <p>
          This deployment has no database. Copy <code>.env.example</code> to{' '}
          <code>.env.local</code> and point it at the household Supabase project, not the business
          one.
        </p>
      </main>
    )
  }

  if (!ready) return <main className="shell">Loading…</main>
  if (!session) return <Auth />

  return <Signed key={session.user.id} session={session} />
}

function Signed({ session }: { session: Session }) {
  const [view, setView] = useState<View>('BOARD')
  const [answers, setAnswers] = useState<Answers>(emptyAnswers())
  const [householdId, setHouseholdId] = useState<string | null>(null)
  /**
   * The screening on screen is the live one, not a frozen copy.
   *
   * There was a `useState<Assessment>` here, set when somebody opened a card,
   * and everything that described a screening read from it: the share
   * package's scope said "nothing has been worked out yet" while the board
   * behind it was showing six areas. One source, which is `live.current`.
   */
  /** Which area's full card is open, so the view opens on it rather than at the top. */
  const [opened, setOpened] = useState<{ domain: string; person: string | null } | null>(null)
  /** The policy whose details are open, by id, so a removal closes it rather than leaving a stale copy. */
  const [viewing, setViewing] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  /**
   * When the record last took a write.
   *
   * There were two states before this, "Saving…" and "Changes save on their
   * own", and the second is what shows before anything has ever been written.
   * So the screen looked identical whether a change had landed or had not
   * started, and there was no moment at which somebody could tell they were
   * safe to close the tab. A test waiting for that text was waiting for
   * nothing, which is how the gap surfaced.
   */
  const [savedAt, setSavedAt] = useState<number | null>(null)
  /**
   * Whether there is a change the record has not taken yet.
   *
   * Without this, "Saved" meant "something was saved at some point", so it was
   * already on screen from the previous write while the current one was still
   * in the debounce. Somebody could read it, close the tab, and lose the thing
   * they had just typed; a test could read it and race the same write.
   *
   * It is the answers moving that sets this, and only a completed save that
   * clears it, so the word on screen is about the change in front of the
   * person rather than about the history of the session.
   */
  const [unsaved, setUnsaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const saved = useRef<Answers | null>(null)
  const editing = useEditingState()

  /**
   * What applies to this household, put down if it is not down already.
   *
   * There is nothing to ask: the basis is the terms, accepted by using the
   * product. What there is to do is record what applied and under which
   * wording, because the privacy policy undertakes to answer an access request
   * with that and a later change to the retention period would otherwise
   * silently restate what every existing household was subject to.
   *
   * It never blocks. A record of what applied is not a permission to proceed,
   * so a failed insert leaves a gap in the audit and the household still sees
   * its own screening.
   */
  const applied = useCallback(async (id: string): Promise<void> => {
    try {
      await applyTerms(id, await listConsents(id))
    } catch (thrown) {
      console.error('The terms in force were not recorded for this household.', thrown)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const held = await loadRecord(session.user.id)
        if (cancelled) return
        if (held) {
          setHouseholdId(held.householdId)
          setAnswers(held.answers)
          saved.current = held.answers
          await applied(held.householdId)
        } else {
          const household = await currentHousehold()
          if (cancelled) return
          setHouseholdId(household.id)
          // One member to begin with, because a household of nobody is not a
          // state anybody meant to be in.
          const start: Answers = {
            ...emptyAnswers(),
            household_name: household.name,
            people: [
              { id: crypto.randomUUID(), label: 'You', role: 'SELF', facts: emptyPersonFacts() },
            ],
          }
          setAnswers(start)
          /**
           * What is on the record so far, which for a new household is nothing.
           *
           * The autosave effect refuses to run while this is null, so that a
           * save cannot fire before the record has been read. The loaded branch
           * above sets it and this one did not, which meant a brand-new
           * household never saved anything at all: every answer somebody typed
           * sat in React state until they reloaded and lost it.
           *
           * Deliberately not `start`. The member created a line above has not
           * been written either, and a baseline that already contained them
           * would make the first diff empty and leave them unwritten until
           * something else changed.
           */
          saved.current = { ...emptyAnswers(), household_name: household.name }
          await applied(household.id)
        }
      } catch (thrown) {
        if (!cancelled) setError(thrown instanceof Error ? thrown.message : 'Something went wrong.')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [session.user.id])

  /**
   * Saved on a pause, and never while a field is being typed into.
   *
   * The settle window is the business app's, and it is worth keeping: a figure
   * half-entered and then abandoned should not become a recorded answer, and
   * focus alone is not enough to tell the two apart.
   */
  useEffect(() => {
    if (!householdId || saved.current === null) return
    if (answers !== saved.current) setUnsaved(true)
    const timer = setTimeout(() => {
      const withholding = editing.unsettled()
      setSaving(true)
      void saveRecord({ householdId, previous: saved.current, current: answers, withholding })
        .then(() => {
          saved.current = answers
          setSavedAt(Date.now())
          setUnsaved(false)
          setError(null)
        })
        .catch((thrown: unknown) =>
          setError(thrown instanceof Error ? thrown.message : 'That did not save.'),
        )
        .finally(() => setSaving(false))
    }, 900)
    return () => clearTimeout(timer)
  }, [answers, householdId, editing])

  /**
   * Whether a board can be drawn at all.
   *
   * Somebody in the household, and nothing else. Every area is about a person,
   * so a board of six areas about nobody is six empty cards.
   *
   * It read two agreements as well, which is what the four ticks were for. The
   * basis is the terms now, so there is nothing to wait for: a household that
   * has signed in and has a member in it gets a board. The board was also the
   * thing those ticks could take away, silently, from the foot of another
   * screen.
   */
  const screening = householdId !== null && hasSomebody(answers)

  /**
   * The board's screening, recomputed as the answers settle.
   *
   * The rules run on the server, so this is a request per settled answer rather
   * than a recalculation. `useAssessment` keeps the previous result on screen
   * while the next one is in flight, because a board that blanked on every
   * keystroke would say there is nothing to report when there is.
   */
  const live = useAssessment(screening ? answers : null)

  /** The screening everything on screen describes. One source. */
  const screened = live.current

  /**
   * The screening kept, and only the ones somebody was actually shown in full.
   *
   * Framework section 10.3 says every result is stored, which was easy when a
   * result was something a button produced. A live board produces one per
   * settled answer, and storing all of them would fill the table with
   * keystrokes. So a snapshot is written when a household opens the full card
   * for an area, which is the moment a result stops being a glance at a board
   * and becomes something read, and it is deduplicated by content so opening
   * three cards in a row records one screening rather than three.
   */
  const stored = useRef<string | null>(null)

  const keep = useCallback(
    async (produced: Assessment) => {
      if (!householdId) return
      const key = keyFor(produced)
      if (stored.current === key) return
      stored.current = key
      try {
        await saveAssessment({
          householdId,
          householdName: answers.household_name,
          answers,
          assessment: produced,
          explanationVersion: EXPLANATION_VERSION,
        })
      } catch (thrown) {
        setError(thrown instanceof Error ? thrown.message : 'That screening was not recorded.')
      }
    },
    [answers, householdId],
  )

  /**
   * Open the full five-part card for one area.
   *
   * The board carries a line per area; this is the rest of what framework
   * section 9.1 asks for, which does not fit on a row and should not be
   * squeezed onto one.
   */
  const openFinding = useCallback(
    (domain: string, person: string | null) => {
      const produced = live.current
      if (!produced) return
      setOpened({ domain, person })
      void keep(produced)
    },
    [keep, live],
  )

  const onAddPolicy = useCallback(
    async (policy: Policy) => {
      if (!householdId) return
      const next = [...answers.policies, policy]
      setSaving(true)
      try {
        await savePolicies(householdId, next)
        setAnswers((held) => ({ ...held, policies: next }))
        setError(null)
      } catch (thrown) {
        setError(thrown instanceof Error ? thrown.message : 'That policy was not saved.')
      } finally {
        setSaving(false)
      }
    },
    [answers.policies, householdId],
  )

  /**
   * Who owns a policy, set after it is on the record.
   *
   * The review step asks, and it is easy to leave on "nobody": it sits below a
   * list of quoted lines and defaults to not matched, and once a policy was
   * added there was no way back to it. So the sheet that shows the policy can
   * answer it. Written the way every other policy change is, as the whole list,
   * because a schedule is a document with parts and not a value with an age.
   *
   * It changes no figure. The engine counts cover by who it insures and never
   * reads the owner, so this records who holds the contract and nothing else.
   */
  const onSetOwner = useCallback(
    async (id: string, owner: string | null) => {
      if (!householdId) return
      const next = answers.policies.map((p) =>
        p.id === id ? { ...p, policyholder_person_id: owner } : p,
      )
      setSaving(true)
      try {
        await savePolicies(householdId, next)
        setAnswers((held) => ({ ...held, policies: next }))
        setError(null)
      } catch (thrown) {
        setError(thrown instanceof Error ? thrown.message : 'That was not saved.')
      } finally {
        setSaving(false)
      }
    },
    [answers.policies, householdId],
  )

  const onRemovePolicy = useCallback(
    async (id: string) => {
      if (!householdId) return
      const next = answers.policies.filter((p) => p.id !== id)
      setSaving(true)
      try {
        await savePolicies(householdId, next)
        setAnswers((held) => ({ ...held, policies: next }))
        setError(null)
      } catch (thrown) {
        setError(thrown instanceof Error ? thrown.message : 'That policy was not removed.')
      } finally {
        setSaving(false)
      }
    },
    [answers.policies, householdId],
  )


  return (
    <EditingContext.Provider value={editing}>
      {/*
        Inside the column, not above it.

        The header was a sibling of `.shell`, which is a centred 1160px column
        from the shared tokens, so on any wide screen the wordmark sat against
        the left edge of the window and "Sign out" against the right while
        everything below them was centred. The business app puts its header
        inside the same column, which is the only way the two line up.
      */}
      <main className="shell">
        <header className="app-header">
          {/*
            The mark, not the word. The business app's header is the logo and a
            line of small capitals naming what the product screens, and this
            said "Asvise family protection" in running text instead: the one
            place both products are unmistakably the same company was the one
            place they looked unrelated. The asset and the `.asvise-mark` rule
            were already here, used only on the sign-in screen.
          */}
          <p className="wordmark">
            <span className="asvise-mark" role="img" aria-label="Asvise" />
            <span className="wordmark-what">family protection</span>
          </p>

          {/*
            One cluster rather than three loose children.

            `space-between` distributes the gaps between every child, so adding
            the save state to a header of three pushed it out to the middle of
            the page, a long way from the account it belongs beside. Grouped,
            the rule means what it reads as: wordmark on one side, account on
            the other.

            The save state is here because the first screen and the sheet over
            the board both take answers and neither had anywhere to say so.
          */}
          <div className="account">
            <span>{session.user.email}</span>
            <button type="button" className="link" onClick={() => void supabase?.auth.signOut()}>
              Sign out
            </button>
          </div>
        </header>

        {error && <p className="error">{error}</p>}

        {/*
          * Nothing is interactive until there is a record to write to.
          *
          * The form used to render immediately, while `currentHousehold()` was
          * still resolving, and the answers it was bound to were replaced the
          * moment it did. Somebody adding a member in that first instant
          * watched it appear and then vanish, with no error and nothing in the
          * record; a consent ticked there was dropped silently. Both were the
          * same bug wearing different clothes, and the fix is to not offer a
          * form that has nowhere to put anything.
          */}
        {view === 'RECORD' && householdId === null && !error && (
          <p className="lead">Getting your family…</p>
        )}

        {view === 'BOARD' && householdId === null && !error && (
          <p className="lead">Getting your family…</p>
        )}

        {/*
          The first screen, and the only one that is not the board.
          
          The business app asks one question here, nature of business, because
          that single answer turns an industry table into a set of possible
          exposures and the board opens populated rather than as eight empty
          cards. The household equivalent is who lives here: every area is about
          a person, and members are what give the six areas their subjects.
          
          The assessment consent is on this screen for the same reason. It used
          to sit behind a button labelled "see what this means", which worked
          while a screening was something somebody asked for. A board is a
          screening, so the agreement has to come before the board rather than
          after it.
        */}
        {/*
          A household with nobody in it, which is the only thing left between
          signing in and the board.
          
          It used to be the consent screen as well, and the name and the members
          were asked here because there was a screen to ask them on. There is no
          agreement to collect now, so this is reached only by a household that
          somehow has no members, and everything that is a question has moved to
          the record where the other questions are.
        */}
        {view === 'BOARD' && householdId !== null && !screening && (
          <>
            <h1>Your family</h1>
            <p className="lead">
              We work out what each person here would be exposed to, so who lives with you is the
              one thing we need before anything else.
            </p>

            <People answers={answers} onChange={setAnswers} />

            <p className="note">Add at least one person and the board opens.</p>

            <div className="actions">
              <SaveStatus saving={saving} unsaved={unsaved} savedAt={savedAt} />
            </div>
          </>
        )}

        {view === 'BOARD' && screening && (
          <Board
              assessment={screened}
              answers={answers}
              onChange={setAnswers}
              lagging={live.lagging}
              failed={live.failed}
              onRetry={live.retry}
              onOpenFinding={openFinding}
              actions={
                <>
                  <button type="button" className="secondary" onClick={() => setView('RECORD')}>
                    Your family record
                  </button>
                  {/*
                    No count on it. How many policies are on the record is a
                    figure about the record rather than about the household's
                    protection, and the board is six areas and what each of them
                    would need: a route out of it should say where it goes. The
                    screen it opens lists them, which is where a number means
                    something.
                  */}
                  <button type="button" className="secondary" onClick={() => setView('DOCUMENTS')}>
                    {answers.policies.length === 0 ? 'Add a policy document' : 'Your policies'}
                  </button>

                </>
            }
          />
        )}

        {/*
          No notices anywhere. What each part of the product does is in the
          terms of use, and the screens say for themselves what they are doing:
          the board carries the engine's own disclaimer that it is an
          educational screening and not a recommendation, and the upload panel
          says what happens to a file. The record keeps the account of what
          applies to it, with dates and wording versions, which is the audit
          surface rather than a notice.
        */}
        {view === 'BOARD' && screening && (
          <div className="actions">
            <SaveStatus saving={saving} unsaved={unsaved} savedAt={savedAt} />
          </div>
        )}

        {view === 'RECORD' && householdId !== null && (
          <>
            <button type="button" className="link back" onClick={() => setView('BOARD')}>
              Back to the board
            </button>
            <h1>Your family record</h1>
            {/*
              One screen, where there were two. "Every question" listed the
              questions with what had been answered; "What we hold" listed what
              was held. Both were rows, both were about the same record, and a
              household had to know that one of them was for reading and the
              other for changing. Everything we hold is everything we asked, so
              there was never a second thing to show.
            */}
            <p className="lead">
              Every answer we hold, when it was given, and which areas read it, in the four groups
              the questionnaire is set out in. Pick a line to change it, or take a group at a time.
              Nothing writes to this record but your family. Nothing here is a recommendation,
              and we never ask about anyone's health.
            </p>

            <Household
              answers={answers}
              onChange={setAnswers}
              assessment={screened}
              at={Date.now()}
            />

            {/*
              No policies on this screen. There was a group listing each one
              with the page behind every figure, and a button to manage them,
              and both were a second place for something that has its own
              screen: the board offers the documents, the findings screen quotes
              the line and the page a figure came from, and the import review is
              where a figure is accepted in the first place.
              
              What is only there and nowhere else is how a policy got onto the
              record: read from a document and accepted line by line, or typed
              by hand, with the date it was confirmed. That belongs beside the
              policies if it belongs anywhere.
            */}

            {/*
              No account of what applies to this record on the screen. The
              terms say what the product may do, and a list of three rows
              repeating that under dates and a wording version is the audit
              trail printed at somebody who did not ask for it. The rows
              themselves are still written, which is where an access request is
              answered from.
            */}

            {/* No second way back. The link above the heading is the one every other
                screen has, and a primary button here made leaving look like the
                thing to do after answering, when answering saves as it goes. The
                foot keeps the save status, which is what it is for. */}
            <div className="actions">
              <SaveStatus saving={saving} unsaved={unsaved} savedAt={savedAt} />
            </div>
          </>
        )}

        {view === 'DOCUMENTS' && (
          <>
            <button type="button" className="link back" onClick={() => setView('BOARD')}>
              Back to the board
            </button>
            <h1>Your policies</h1>
            <p className="lead">
              What a policy covers is read from the document you upload, and you check each line
              before it is added. Benefits that draw on the same sum are never added together.
            </p>

            {answers.policies.length > 0 && (
              <ul className="policy-list">
                {answers.policies.map((policy) => (
                  <li key={policy.id}>
                    {/*
                      The text is the way in, and Remove stays beside it rather
                      than inside it: a button within a button is invalid and
                      takes the keyboard to the wrong one.
                    */}
                    <button
                      type="button"
                      className="policy-open"
                      onClick={() => setViewing(policy.id)}
                      aria-label={`Details of ${policy.product_name || 'this policy'}`}
                    >
                      <strong>{policy.product_name || 'A policy'}</strong>
                      <span className="policy-line">
                        {[policy.insurer, policy.policy_number].filter(Boolean).join(' · ')}
                      </span>
                      <span className="policy-line">
                        {policy.benefits.length}{' '}
                        {policy.benefits.length === 1 ? 'benefit' : 'benefits'}
                        {policy.benefits.some((b) => b.insured_person_id === null) &&
                          ', some not matched to anybody'}
                      </span>
                      {(policy.review_required ?? []).length > 0 && (
                        <span className="policy-review">
                          Needs a look: {(policy.review_required ?? []).join(' ')} Amounts from this
                          document are reported and counted towards no shortfall.
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      className="link danger"
                      disabled={saving}
                      onClick={() => void onRemovePolicy(policy.id)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <PolicyImport people={answers.people} onAdd={(policy) => void onAddPolicy(policy)} />

            {viewing !== null &&
              (() => {
                const policy = answers.policies.find((p) => p.id === viewing)
                return policy ? (
                  <PolicySheet
                    policy={policy}
                    people={answers.people}
                    busy={saving}
                    onOwner={(owner) => void onSetOwner(policy.id, owner)}
                    onClose={() => setViewing(null)}
                  />
                ) : null
              })()}

            {/*
              Asked here, which is what `Consent`'s own comment always claimed:
              reading documents and sharing are asked where those things happen.
              They were not. This one could be given nowhere but the foot of the
              household record, and the panel above refused a file and sent
              somebody to another screen to hunt for the checkbox that would
              let it in. The question belongs beside the thing it permits,
              where it can be pictured.
            */}
          </>
        )}

        {/*
          One area, in a sheet over the board, rather than a screen of all of
          them opened at the one that was clicked. The order a row sits in is
          part of reading it, and a screen that replaced the board took the
          order away to show the thing the order was about.
          
          Opening it is also what stores the screening, which has not changed: a
          live board produces a result per settled answer, and a snapshot is
          written when somebody opens a card, which is the moment a result stops
          being a glance and becomes something read.
        */}
        {opened && screened && (
          <FindingSheet
            assessment={screened}
            finding={
              screened.findings.find(
                (f) =>
                  f.domain === opened.domain && (f.person_id ?? null) === (opened.person ?? null),
              ) ?? screened.findings[0]!
            }
            onClose={() => setOpened(null)}
          />
        )}

      </main>
    </EditingContext.Provider>
  )
}

